import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material';
import { getApiUrl } from '../config';

function ChangePassword() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // پاک کردن پیام‌های خطا و موفقیت هنگام تغییر
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    if (!formData.currentPassword) {
      setError('لطفاً رمز عبور فعلی را وارد کنید');
      return false;
    }
    if (!formData.newPassword) {
      setError('لطفاً رمز عبور جدید را وارد کنید');
      return false;
    }
    if (formData.newPassword.length < 6) {
      setError('رمز عبور جدید باید حداقل 6 کاراکتر باشد');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('رمز عبور جدید و تکرار آن یکسان نیستند');
      return false;
    }
    if (formData.currentPassword === formData.newPassword) {
      setError('رمز عبور جدید نباید با رمز عبور فعلی یکسان باشد');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('رمز عبور با موفقیت تغییر یافت');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(data.error || 'خطا در تغییر رمز عبور');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', fontFamily: 'Vazir' }}>
      <Paper sx={{ p: 4, mt: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LockIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', fontFamily: 'Vazir' }}>
            تغییر رمز عبور
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontFamily: 'Vazir' }}>
          برای تغییر رمز عبور، رمز عبور فعلی و رمز عبور جدید را وارد کنید.
        </Typography>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, fontFamily: 'Vazir' }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3, fontFamily: 'Vazir' }}>
            {success}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          {/* Current Password */}
          <TextField
            fullWidth
            label="رمز عبور فعلی"
            type={showPasswords.currentPassword ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={handleInputChange('currentPassword')}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePasswordVisibility('currentPassword')}
                    edge="end"
                  >
                    {showPasswords.currentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ fontFamily: 'Vazir' }}
          />

          {/* New Password */}
          <TextField
            fullWidth
            label="رمز عبور جدید"
            type={showPasswords.newPassword ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={handleInputChange('newPassword')}
            margin="normal"
            required
            helperText="حداقل 6 کاراکتر"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePasswordVisibility('newPassword')}
                    edge="end"
                  >
                    {showPasswords.newPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ fontFamily: 'Vazir' }}
          />

          {/* Confirm Password */}
          <TextField
            fullWidth
            label="تکرار رمز عبور جدید"
            type={showPasswords.confirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePasswordVisibility('confirmPassword')}
                    edge="end"
                  >
                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ fontFamily: 'Vazir' }}
          />

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                fontFamily: 'Vazir',
                minWidth: 120,
                py: 1.5
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'تغییر رمز عبور'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default ChangePassword;
